/** Process map (ADR-0056): plain language for people who do not build agents. */
export default {
  en: {
    tab: "Process map",
    intro: "How requests to an agent move through its steps: where they wait for people, where they are refused or fail, and how long each step takes.",
    controls: {
      agent: "Agent",
      version: "Version",
      allVersions: "All versions",
      range: "Time range",
      last1: "Last 24 hours",
      last7: "Last 7 days",
      last30: "Last 30 days",
      last90: "Last 90 days",
      view: "View",
      map: "Map",
      table: "Table",
      refresh: "Refresh",
    },
    pickAgent: "Choose an agent to see how its requests flow.",
    noAgents: "There are no agents in this organisation yet.",
    empty: "No requests for this agent in the selected time range.",
    error: "The process map could not be loaded.",
    truncated: "Showing the most recent {{shown}} of {{total}} requests.",
    totals: "{{n}} requests in this period",
    outcome: {
      completed: "Completed",
      refused: "Refused",
      failed: "Failed",
      cancelled: "Cancelled",
      waiting: "Waiting for a person",
      running: "In progress",
    },
    start: "Start",
    node: { retrieve: "Search the documents", think: "Think it through", done: "Done", tool: "Use a connected system", tools: "Use a connected system", approve: "Ask a person to approve", approval: "Ask a person to approve", critique: "Check the answer", reflect: "Check the answer", plan: "Plan the work", execute: "Carry out the plan", act: "Take the next step", route: "Choose who handles it", router: "Choose who handles it", answer: "Answer", respond: "Answer", finish: "Done" },
    type: {
      llm: "AI step",
      tool: "System action",
      router: "Decision",
      human_approval: "Approval by a person",
      human_input: "Question to a person",
      retrieval: "Knowledge search",
      subgraph: "Another agent",
      python: "Calculation",
      parallel: "Parallel paths",
      join: "Paths join",
      end: "End",
    },
    legend: {
      title: "Colour and pattern show how requests ended",
      width: "Band width = number of requests",
      loop: "Dashed arc: went back to an earlier step",
    },
    insights: {
      title: "Top insights",
      biggestWait: "Requests wait longest at “{{step}}”: median wait {{median}}, longest {{max}} ({{n}} requests waited).",
      topRefusal: "Most common refusal: {{reason}}, at “{{step}}” ({{n}} requests).",
      failureRate: "{{pct}} of requests failed ({{n}} of {{total}}). Most common cause: {{reason}}",
      noFailures: "No request failed in this period.",
      completion: "{{pct}} of requests completed ({{n}} of {{total}}).",
      show: "Show",
    },
    reason: {
      serviceFailure: "a service the agent relies on failed or refused the request",
      unknownFailure: "the run stopped with an error",
      guardrail_input: "the request was blocked by the guardrails",
      guardrail_output: "the answer was blocked by the guardrails",
      guardrail: "blocked by the guardrails",
      policy: "the rules did not allow the action",
      approval_rejected: "a person rejected the approval",
      failure: "an error",
      withDetail: "{{reason}} ({{detail}})",
      unknownStep: "an unknown step",
    },
    panel: {
      stepReached: "{{n}} of {{total}} requests reached this step.",
      stepWork: "Typical time here: {{median}}; 9 in 10 within {{p90}}.",
      stepWait: "{{n}} of {{total}} requests waited for a person here; median wait {{median}}, longest {{max}}.",
      edge: "{{n}} of {{total}} requests went from “{{from}}” to “{{to}}”.",
      outcome: "{{n}} of {{total}} requests ended as “{{outcome}}”.",
      start: "{{total}} requests started in this period.",
      endedAs: "How these requests ended",
      reasons: "Reasons",
      requests: "Requests",
      close: "Close",
    },
    table: {
      steps: "Steps",
      bands: "Paths between steps",
      step: "Step",
      kind: "Kind",
      requests: "Requests",
      typical: "Typical time",
      p90: "9 in 10 within",
      wait: "Median wait for a person",
      waitMax: "Longest wait",
      outcomes: "How they ended",
      from: "From",
      to: "To",
      open: "Details for {{name}}",
    },
    requests: {
      requester: "Requester",
      request: "Request",
      started: "Started",
      duration: "Total time",
      wait: "Wait here",
      outcome: "Result",
      run: "Run",
      openRun: "Open run {{id}} in Temporal",
      none: "No requests.",
      hidden: "Hidden",
    },
    a11y: {
      map: "Process map of {{agent}}",
      node: "{{name}}: {{n}} requests. Press Enter for details.",
      band: "From {{from}} to {{to}}: {{n}} requests. Press Enter for details.",
    },
    tooltip: {
      requests: "{{n}} requests",
      median: "Typical time {{v}}",
      wait: "Median wait {{v}}",
    },
  },
  ar: {
    tab: "خريطة سير العمل",
    intro: "كيف تمر الطلبات المقدَّمة إلى الوكيل بخطواته: أين تنتظر الأشخاص، وأين تُرفض أو تفشل، وكم تستغرق كل خطوة.",
    controls: {
      agent: "الوكيل",
      version: "الإصدار",
      allVersions: "كل الإصدارات",
      range: "الفترة الزمنية",
      last1: "آخر 24 ساعة",
      last7: "آخر 7 أيام",
      last30: "آخر 30 يومًا",
      last90: "آخر 90 يومًا",
      view: "طريقة العرض",
      map: "خريطة",
      table: "جدول",
      refresh: "تحديث",
    },
    pickAgent: "اختر وكيلًا لعرض مسار طلباته.",
    noAgents: "لا يوجد وكلاء في هذه الجهة بعد.",
    empty: "لا توجد طلبات لهذا الوكيل في الفترة المحددة.",
    error: "تعذّر تحميل خريطة سير العمل.",
    truncated: "يُعرض أحدث {{shown}} طلب من أصل {{total}}.",
    totals: "عدد الطلبات في هذه الفترة: {{n}}",
    outcome: {
      completed: "مكتمل",
      refused: "مرفوض",
      failed: "فاشل",
      cancelled: "ملغى",
      waiting: "بانتظار شخص",
      running: "قيد التنفيذ",
    },
    start: "البداية",
    node: { retrieve: "البحث في المستندات", think: "التفكير في الطلب", done: "انتهى", tool: "استخدام نظام متصل", tools: "استخدام نظام متصل", approve: "طلب موافقة شخص", approval: "طلب موافقة شخص", critique: "مراجعة الإجابة", reflect: "مراجعة الإجابة", plan: "تخطيط العمل", execute: "تنفيذ الخطة", act: "اتخاذ الخطوة التالية", route: "اختيار من يتولى الطلب", router: "اختيار من يتولى الطلب", answer: "الإجابة", respond: "الإجابة", finish: "انتهى" },
    type: {
      llm: "خطوة ذكاء اصطناعي",
      tool: "إجراء في نظام",
      router: "قرار",
      human_approval: "موافقة شخص",
      human_input: "سؤال لشخص",
      retrieval: "بحث في المعرفة",
      subgraph: "وكيل آخر",
      python: "عملية حسابية",
      parallel: "مسارات متوازية",
      join: "التقاء المسارات",
      end: "النهاية",
    },
    legend: {
      title: "يدل اللون والنقش على نتيجة الطلبات",
      width: "عرض الشريط = عدد الطلبات",
      loop: "القوس المتقطع: العودة إلى خطوة سابقة",
    },
    insights: {
      title: "أبرز الملاحظات",
      biggestWait: "أطول انتظار للطلبات عند «{{step}}»: الوسيط {{median}}، والأطول {{max}} (عدد الطلبات التي انتظرت: {{n}}).",
      topRefusal: "أكثر أسباب الرفض: {{reason}}، عند «{{step}}» (عدد الطلبات: {{n}}).",
      failureRate: "نسبة الطلبات الفاشلة {{pct}} ({{n}} من {{total}}). السبب الأكثر شيوعًا: {{reason}}",
      noFailures: "لم يفشل أي طلب في هذه الفترة.",
      completion: "نسبة الطلبات المكتملة {{pct}} ({{n}} من {{total}}).",
      show: "عرض",
    },
    reason: {
      serviceFailure: "تعطّلت خدمة يعتمد عليها الوكيل أو رفضت الطلب",
      unknownFailure: "توقف التشغيل بسبب خطأ",
      guardrail_input: "حُظر الطلب بموجب قاعدة أمان",
      guardrail_output: "حُظرت الإجابة بموجب قاعدة أمان",
      guardrail: "حُظر بموجب قاعدة أمان",
      policy: "لم تسمح القواعد بتنفيذ الإجراء",
      approval_rejected: "رفض شخص الموافقة",
      failure: "خطأ",
      withDetail: "{{reason}} ({{detail}})",
      unknownStep: "خطوة غير معروفة",
    },
    panel: {
      stepReached: "عدد الطلبات التي وصلت إلى هذه الخطوة: {{n}} من {{total}}.",
      stepWork: "الوقت المعتاد هنا: {{median}}؛ و9 من كل 10 خلال {{p90}}.",
      stepWait: "عدد الطلبات التي انتظرت شخصًا هنا: {{n}} من {{total}}؛ وسيط الانتظار {{median}}، والأطول {{max}}.",
      edge: "عدد الطلبات التي انتقلت من «{{from}}» إلى «{{to}}»: {{n}} من {{total}}.",
      outcome: "عدد الطلبات التي انتهت بحالة «{{outcome}}»: {{n}} من {{total}}.",
      start: "عدد الطلبات التي بدأت في هذه الفترة: {{total}}.",
      endedAs: "كيف انتهت هذه الطلبات",
      reasons: "الأسباب",
      requests: "الطلبات",
      close: "إغلاق",
    },
    table: {
      steps: "الخطوات",
      bands: "المسارات بين الخطوات",
      step: "الخطوة",
      kind: "النوع",
      requests: "الطلبات",
      typical: "الوقت المعتاد",
      p90: "9 من كل 10 خلال",
      wait: "وسيط انتظار شخص",
      waitMax: "أطول انتظار",
      outcomes: "كيف انتهت",
      from: "من",
      to: "إلى",
      open: "تفاصيل {{name}}",
    },
    requests: {
      requester: "مقدّم الطلب",
      request: "الطلب",
      started: "البدء",
      duration: "المدة الكلية",
      wait: "الانتظار هنا",
      outcome: "النتيجة",
      run: "التشغيل",
      openRun: "فتح التشغيل {{id}} في Temporal",
      none: "لا توجد طلبات.",
      hidden: "مخفي",
    },
    a11y: {
      map: "خريطة سير العمل للوكيل {{agent}}",
      node: "{{name}}: عدد الطلبات {{n}}. اضغط Enter لعرض التفاصيل.",
      band: "من {{from}} إلى {{to}}: عدد الطلبات {{n}}. اضغط Enter لعرض التفاصيل.",
    },
    tooltip: {
      requests: "عدد الطلبات: {{n}}",
      median: "الوقت المعتاد {{v}}",
      wait: "وسيط الانتظار {{v}}",
    },
  },
};
